// FRONT-1566: per-conversation role instructions — free text the user sets
// on ONE chat ("You are my interview coach…") that Eddy adds to its context
// for every reply in that conversation only. Read server-side by session id
// (`GET/PUT /api/chat/sessions/:sessionId/role`), so a voice call started
// from this same chat picks the role up automatically with no change to the
// voice mint payload (`use-voice-call.ts`) — the backend resolves it by
// session id the same way for both surfaces.
//
// Modeled on `use-chat-model-mode.ts`'s per-session query/mutation pair, with
// ONE deliberate omission: there is no standing-default mirror.
// `usePickChatModelMode` there writes both the per-session mode AND the
// user's standing default on every pick, so a brand-new chat inherits your
// last Fast/Thoughtful choice. A role must NEVER do that — it is
// per-conversation only, and mirroring a click here into a "standing role"
// would leak one conversation's persona into every chat opened after it. Do
// not "fix" this later by copying that double-write pattern.

import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
  type MutationOptions,
} from '@tanstack/react-query';
import { api } from '~/lib/api-client';

interface ChatRoleResponse {
  instructions: string | null;
}

/** Blank/whitespace instructions CLEAR the role server-side — kept here as
 *  the one place both the dialog and its tests read the server's own cap
 *  from, so the two can't drift. */
export const CHAT_ROLE_MAX_LENGTH = 4000;

export const chatRoleKeys = {
  all: ['chat-role'] as const,
  lists: () => [...chatRoleKeys.all, 'list'] as const,
  detail: (sessionId: string) => [...chatRoleKeys.lists(), sessionId] as const,
};

const STALE_TIME_MS = 30_000;

export async function fetchChatRole(sessionId: string): Promise<string | null> {
  const res = await api.get<ChatRoleResponse>(
    `/api/chat/sessions/${encodeURIComponent(sessionId)}/role`,
  );
  return res.instructions;
}

/** Disabled when there is no sessionId (mirrors `useChatModelMode`). */
export function useChatRole(sessionId: string | undefined) {
  return useQuery<string | null>({
    queryKey: chatRoleKeys.detail(sessionId ?? ''),
    queryFn: () => fetchChatRole(sessionId!),
    enabled: !!sessionId,
    staleTime: STALE_TIME_MS,
  });
}

async function putChatRole(sessionId: string, instructions: string): Promise<string | null> {
  const res = await api.put<ChatRoleResponse>(
    `/api/chat/sessions/${encodeURIComponent(sessionId)}/role`,
    { instructions },
  );
  return res.instructions;
}

/**
 * Pulled out as its own function (same precedent as
 * `pickChatModelModeOptions` in `use-chat-model-mode.ts`) so a test can drive
 * the exact mutation logic through `@tanstack/react-query`'s
 * `MutationObserver` directly against a plain `QueryClient` — this repo's
 * frontend suite has no DOM/jsdom, which rules out mounting the hook itself.
 *
 * Deliberately a single PUT and nothing else: no standing-default mirror (see
 * the file docblock), no optimistic write (the dialog itself holds the local
 * draft and only reads this cache on open, so there's no pill elsewhere that
 * needs an instant flip), no rollback logic to reason about. On success the
 * returned value (server-normalized — a blank/whitespace save comes back as
 * `null`) is written straight into the cache and the detail key is
 * invalidated so any other reader (the header pill) picks it up.
 */
export function setChatRoleOptions(
  qc: QueryClient,
  sessionId: string | undefined,
): MutationOptions<string | null, unknown, string> {
  return {
    mutationFn: (instructions: string) => putChatRole(sessionId!, instructions),
    onSuccess: (instructions) => {
      if (!sessionId) return;
      const key = chatRoleKeys.detail(sessionId);
      qc.setQueryData(key, instructions);
      qc.invalidateQueries({ queryKey: key });
    },
  };
}

export function useSetChatRole(sessionId: string | undefined) {
  const qc = useQueryClient();
  return useMutation(setChatRoleOptions(qc, sessionId));
}
